import {
  getAtomBalance,
  getBalanceHint,
  isEquationBalanced,
  isSimplestCoefficientRatio,
  parseCompound,
  parseEquation,
  type AtomBalance,
  type ParsedEquation,
} from './chemicalEquation';

export type ConservationMode = 'word' | 'mass';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const CONSERVATION_OF_MASS_TOOL_IDENTITY = {
  id: 'conservation-of-mass',
  toolKey: 'chemistry-conservation-of-mass',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

/** Both modes start at Year 9; the learner chooses. The default is `word`. */
export const CONSERVATION_MODES: readonly { mode: ConservationMode; fromYear: number }[] = [
  { mode: 'word', fromYear: 9 },
  { mode: 'mass', fromYear: 9 },
];

export const DEFAULT_CONSERVATION_MODE: ConservationMode = 'word';

export interface ConservationDataProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: 'pending-teaching-review';
}

export const CONSERVATION_DATA_PROVENANCE: ConservationDataProvenance = {
  sources: [
    'IUPAC abridged standard atomic weights (conventional values, 2021)',
    'IUPAC nomenclature and conventional formulae for common Year 9 substances',
    'Standard secondary chemistry conservation-of-mass demonstrations (open and closed systems)',
  ],
  conditions:
    'SLC: 25 °C and 100 kPa. Balance readings are model values to 0.01 g calculated from the stated amounts, not measurements.',
  reviewStatus: 'pending-teaching-review',
};

export const MODEL_BOUNDARIES: string[] = [
  'Masses are calculated from the stated amount of the limiting reactant and the balanced equation, assuming it reacts completely.',
  'In an open system, the model assumes every gas product escapes and every gas reactant comes from the air. In real experiments some gas stays dissolved.',
  'Closed-system cases use a rigid sealed flask or bottle. An inflating balloon pushes air aside, so a real balance reading can drop a little because of buoyancy; the model ignores this.',
  'Heating, burning, acids and sealed containers that build pressure need a teacher, a risk assessment and safety glasses.',
];

/** IUPAC abridged standard atomic weights (g/mol), conventional values. */
export const ATOMIC_MASSES: Readonly<Record<string, number>> = {
  H: 1.008,
  C: 12.011,
  O: 15.999,
  Na: 22.99,
  Mg: 24.305,
  S: 32.06,
  Cl: 35.45,
  Ca: 40.078,
  Fe: 55.845,
  Cu: 63.546,
  Zn: 65.38,
};

/** A school top-pan balance reads to 0.01 g. Changes smaller than half a division read as "same". */
export const BALANCE_RESOLUTION_G = 0.01;
const SAME_THRESHOLD_G = BALANCE_RESOLUTION_G / 2;

export const MIN_COEFFICIENT = 1;
export const MAX_COEFFICIENT = 12;

/** Molar mass in g/mol from the formula. Returns null (fail closed) for unknown symbols or formulae. */
export function molarMass(formula: string): number | null {
  const parsed = parseCompound(formula);
  if (!parsed.compound || parsed.compound.coefficient !== 1) return null;
  let total = 0;
  for (const [symbol, count] of Object.entries(parsed.compound.atoms)) {
    const mass = ATOMIC_MASSES[symbol];
    if (mass === undefined) return null;
    total += mass * count;
  }
  return total;
}

// ---------------------------------------------------------------------------------------------
// Word equations
// ---------------------------------------------------------------------------------------------

export type SubstanceRole = 'reactant' | 'product';

export interface WordSubstance {
  name: string;
  formula: string;
  role: SubstanceRole;
  /** Formula choices offered to the learner. Always includes `formula`. */
  formulaOptions: string[];
  /** Specific feedback for each wrong formula option. */
  wrongFormulaFeedback: Record<string, string>;
}

export interface WordEquationCase {
  id: string;
  title: string;
  wordEquation: string;
  /** Substances in word-equation order: reactants, then products. */
  substances: WordSubstance[];
  /** Simplest whole-number coefficients in the same order as `substances`. */
  balanced: number[];
  note: string;
}

const single = (name: string, formula: string, role: SubstanceRole): WordSubstance => ({
  name,
  formula,
  role,
  formulaOptions: [formula],
  wrongFormulaFeedback: {},
});

const OXYGEN_GAS: WordSubstance = {
  name: 'oxygen',
  formula: 'O2',
  role: 'reactant',
  formulaOptions: ['O', 'O2'],
  wrongFormulaFeedback: {
    O: 'Oxygen gas is made of O₂ molecules: two oxygen atoms bonded together. A single O atom is not how oxygen exists in air.',
  },
};

const HYDROGEN_PRODUCT: WordSubstance = {
  name: 'hydrogen',
  formula: 'H2',
  role: 'product',
  formulaOptions: ['H', 'H2'],
  wrongFormulaFeedback: {
    H: 'Hydrogen gas is made of H₂ molecules, like the other diatomic elements (H₂, N₂, O₂, F₂, Cl₂, Br₂, I₂).',
  },
};

const WATER_PRODUCT: WordSubstance = {
  name: 'water',
  formula: 'H2O',
  role: 'product',
  formulaOptions: ['H2O', 'H2O2'],
  wrongFormulaFeedback: {
    H2O2: 'H₂O₂ is hydrogen peroxide, a different substance. Water is H₂O.',
  },
};

const CARBON_DIOXIDE_PRODUCT: WordSubstance = {
  name: 'carbon dioxide',
  formula: 'CO2',
  role: 'product',
  formulaOptions: ['CO', 'CO2', 'C2O'],
  wrongFormulaFeedback: {
    CO: 'CO is carbon monoxide. The prefix “di” in dioxide means two oxygen atoms: CO₂.',
    C2O: '“Dioxide” describes two oxygen atoms, not two carbon atoms. Carbon dioxide is CO₂.',
  },
};

export const WORD_EQUATION_CASES: WordEquationCase[] = [
  {
    id: 'magnesium-oxygen',
    title: 'Burning magnesium',
    wordEquation: 'magnesium + oxygen → magnesium oxide',
    substances: [
      single('magnesium', 'Mg', 'reactant'),
      OXYGEN_GAS,
      {
        name: 'magnesium oxide',
        formula: 'MgO',
        role: 'product',
        formulaOptions: ['MgO', 'MgO2', 'Mg2O'],
        wrongFormulaFeedback: {
          MgO2: 'Magnesium forms Mg²⁺ ions and oxide is O²⁻. The charges cancel 1 : 1, so the formula is MgO.',
          Mg2O: 'Mg²⁺ and O²⁻ have equal and opposite charges, so one of each is needed: MgO.',
        },
      },
    ],
    balanced: [2, 1, 2],
    note: 'Two magnesium atoms are needed for the two oxygen atoms in one O₂ molecule.',
  },
  {
    id: 'hydrogen-oxygen',
    title: 'Hydrogen pop test',
    wordEquation: 'hydrogen + oxygen → water',
    substances: [
      { ...HYDROGEN_PRODUCT, role: 'reactant' },
      OXYGEN_GAS,
      WATER_PRODUCT,
    ],
    balanced: [2, 1, 2],
    note: 'This is the reaction behind the squeaky pop when a lit splint meets hydrogen.',
  },
  {
    id: 'carbon-oxygen',
    title: 'Burning charcoal',
    wordEquation: 'carbon + oxygen → carbon dioxide',
    substances: [single('carbon', 'C', 'reactant'), OXYGEN_GAS, CARBON_DIOXIDE_PRODUCT],
    balanced: [1, 1, 1],
    note: 'Already balanced with a coefficient of 1 for every substance.',
  },
  {
    id: 'sodium-chlorine',
    title: 'Making salt from its elements',
    wordEquation: 'sodium + chlorine → sodium chloride',
    substances: [
      single('sodium', 'Na', 'reactant'),
      {
        name: 'chlorine',
        formula: 'Cl2',
        role: 'reactant',
        formulaOptions: ['Cl', 'Cl2'],
        wrongFormulaFeedback: {
          Cl: 'Chlorine gas is diatomic: Cl₂ molecules, like oxygen and hydrogen.',
        },
      },
      {
        name: 'sodium chloride',
        formula: 'NaCl',
        role: 'product',
        formulaOptions: ['NaCl', 'NaCl2', 'Na2Cl'],
        wrongFormulaFeedback: {
          NaCl2: 'Na⁺ and Cl⁻ have charges of 1+ and 1−, so they combine 1 : 1 as NaCl. The 2 in Cl₂ belongs to chlorine gas, not the compound.',
          Na2Cl: 'Na⁺ and Cl⁻ combine 1 : 1, so sodium chloride is NaCl.',
        },
      },
    ],
    balanced: [2, 1, 2],
    note: 'One Cl₂ molecule gives the chlorine for two NaCl units, so two sodium atoms are needed.',
  },
  {
    id: 'zinc-hydrochloric',
    title: 'Zinc in hydrochloric acid',
    wordEquation: 'zinc + hydrochloric acid → zinc chloride + hydrogen',
    substances: [
      single('zinc', 'Zn', 'reactant'),
      single('hydrochloric acid', 'HCl', 'reactant'),
      {
        name: 'zinc chloride',
        formula: 'ZnCl2',
        role: 'product',
        formulaOptions: ['ZnCl', 'ZnCl2'],
        wrongFormulaFeedback: {
          ZnCl: 'Zinc forms Zn²⁺ ions. Two Cl⁻ ions are needed to balance the charge: ZnCl₂.',
        },
      },
      HYDROGEN_PRODUCT,
    ],
    balanced: [1, 2, 1, 1],
    note: 'Two HCl supply the two chlorine atoms in ZnCl₂ and the two hydrogen atoms in H₂.',
  },
  {
    id: 'calcium-carbonate-heat',
    title: 'Heating limestone',
    wordEquation: 'calcium carbonate → calcium oxide + carbon dioxide',
    substances: [
      {
        name: 'calcium carbonate',
        formula: 'CaCO3',
        role: 'reactant',
        formulaOptions: ['CaCO3', 'CaCO2', 'CaC'],
        wrongFormulaFeedback: {
          CaCO2: 'The carbonate ion is CO₃²⁻: one carbon and three oxygen atoms. Calcium carbonate is CaCO₃.',
          CaC: '“Carbonate” means the CO₃²⁻ ion, not carbon on its own. Calcium carbonate is CaCO₃.',
        },
      },
      single('calcium oxide', 'CaO', 'product'),
      CARBON_DIOXIDE_PRODUCT,
    ],
    balanced: [1, 1, 1],
    note: 'A decomposition: one reactant breaks into two products. It is already balanced.',
  },
  {
    id: 'methane-oxygen',
    title: 'Burning natural gas',
    wordEquation: 'methane + oxygen → carbon dioxide + water',
    substances: [
      {
        name: 'methane',
        formula: 'CH4',
        role: 'reactant',
        formulaOptions: ['CH4', 'CH2'],
        wrongFormulaFeedback: {
          CH2: 'Carbon makes four bonds. Methane is one carbon atom bonded to four hydrogen atoms: CH₄.',
        },
      },
      OXYGEN_GAS,
      CARBON_DIOXIDE_PRODUCT,
      WATER_PRODUCT,
    ],
    balanced: [1, 2, 1, 2],
    note: 'Balance carbon first, then hydrogen, then oxygen last because O₂ appears alone.',
  },
  {
    id: 'iron-sulfur',
    title: 'Heating iron with sulfur',
    wordEquation: 'iron + sulfur → iron(II) sulfide',
    substances: [
      single('iron', 'Fe', 'reactant'),
      single('sulfur', 'S', 'reactant'),
      {
        name: 'iron(II) sulfide',
        formula: 'FeS',
        role: 'product',
        formulaOptions: ['FeS', 'FeSO4', 'Fe2S'],
        wrongFormulaFeedback: {
          FeSO4: 'FeSO₄ is iron(II) sulfate. The ending “-ide” means the compound contains only the two named elements.',
          Fe2S: 'Iron(II) means Fe²⁺, and sulfide is S²⁻. The charges cancel 1 : 1, so the formula is FeS.',
        },
      },
    ],
    balanced: [1, 1, 1],
    note: 'Iron and sulfur combine to form a new compound that a magnet no longer attracts.',
  },
  {
    id: 'calcium-water',
    title: 'Calcium in water',
    wordEquation: 'calcium + water → calcium hydroxide + hydrogen',
    substances: [
      single('calcium', 'Ca', 'reactant'),
      { ...WATER_PRODUCT, role: 'reactant' },
      {
        name: 'calcium hydroxide',
        formula: 'Ca(OH)2',
        role: 'product',
        formulaOptions: ['CaOH', 'Ca(OH)2', 'CaOH2'],
        wrongFormulaFeedback: {
          CaOH: 'Calcium forms Ca²⁺, so it needs two hydroxide ions (OH⁻): Ca(OH)₂.',
          CaOH2: 'The 2 must apply to the whole hydroxide ion, so brackets are needed: Ca(OH)₂.',
        },
      },
      HYDROGEN_PRODUCT,
    ],
    balanced: [1, 2, 1, 1],
    note: 'The bracket in Ca(OH)₂ means two O and two H atoms on the product side.',
  },
  {
    id: 'neutralisation',
    title: 'Neutralising an acid',
    wordEquation: 'sodium hydroxide + hydrochloric acid → sodium chloride + water',
    substances: [
      {
        name: 'sodium hydroxide',
        formula: 'NaOH',
        role: 'reactant',
        formulaOptions: ['NaOH', 'Na(OH)2'],
        wrongFormulaFeedback: {
          'Na(OH)2': 'Sodium forms Na⁺, so one hydroxide ion (OH⁻) balances it: NaOH.',
        },
      },
      single('hydrochloric acid', 'HCl', 'reactant'),
      single('sodium chloride', 'NaCl', 'product'),
      WATER_PRODUCT,
    ],
    balanced: [1, 1, 1, 1],
    note: 'Acid + base → salt + water. Every coefficient is 1.',
  },
];

/** Substances in a fixed alphabetical order, so the learner must read the arrow to find roles. */
export function substancesForSorting(entry: WordEquationCase): WordSubstance[] {
  return [...entry.substances].sort((left, right) => left.name.localeCompare(right.name));
}

export interface RoleCheck {
  correct: boolean;
  wrongNames: string[];
  feedback: string;
}

/** Fails closed (all wrong) if a role is missing. */
export function checkRoles(entry: WordEquationCase, roles: Record<string, SubstanceRole | undefined>): RoleCheck {
  const wrongNames = entry.substances
    .filter((substance) => roles[substance.name] !== substance.role)
    .map((substance) => substance.name);
  if (wrongNames.length === 0) {
    return {
      correct: true,
      wrongNames,
      feedback:
        'Reactants are written before the arrow and are used up. Products are written after the arrow and are made.',
    };
  }
  return {
    correct: false,
    wrongNames,
    feedback: `Check ${wrongNames.join(', ')}. Everything to the left of the arrow is a reactant (starting substance); everything to the right is a product (new substance).`,
  };
}

export interface FormulaCheck {
  correct: boolean;
  wrongNames: string[];
  messages: string[];
}

/** Fails closed: a missing or unoffered formula counts as wrong. */
export function checkFormulas(entry: WordEquationCase, formulas: Record<string, string | undefined>): FormulaCheck {
  const messages: string[] = [];
  const wrongNames: string[] = [];
  entry.substances.forEach((substance) => {
    const chosen = formulas[substance.name];
    if (chosen === substance.formula) return;
    wrongNames.push(substance.name);
    if (chosen === undefined || !substance.formulaOptions.includes(chosen)) {
      messages.push(`Choose a formula for ${substance.name}.`);
      return;
    }
    messages.push(substance.wrongFormulaFeedback[chosen] ?? `${chosen} is not the formula for ${substance.name}.`);
  });
  return { correct: wrongNames.length === 0, wrongNames, messages };
}

export type BalanceStatus = 'invalid' | 'unbalanced' | 'not-simplest' | 'balanced';

export interface BalanceCheck {
  status: BalanceStatus;
  atoms: AtomBalance[];
  message: string;
}

function coefficientsValid(values: readonly number[], expectedLength: number): boolean {
  return (
    values.length === expectedLength &&
    values.every((value) => Number.isInteger(value) && value >= MIN_COEFFICIENT && value <= MAX_COEFFICIENT)
  );
}

export function wordEquationInput(entry: WordEquationCase, coefficients: readonly number[]): string | null {
  if (!coefficientsValid(coefficients, entry.substances.length)) return null;
  const format = (substance: WordSubstance, index: number) =>
    `${coefficients[index] > 1 ? coefficients[index] : ''}${substance.formula}`;
  const left: string[] = [];
  const right: string[] = [];
  entry.substances.forEach((substance, index) =>
    (substance.role === 'reactant' ? left : right).push(format(substance, index)),
  );
  return `${left.join(' + ')} -> ${right.join(' + ')}`;
}

/** Checks coefficients with the shared balance helper. Fails closed on invalid input. */
export function checkWordBalance(entry: WordEquationCase, coefficients: readonly number[]): BalanceCheck {
  const input = wordEquationInput(entry, coefficients);
  const parsed = input ? parseEquation(input) : null;
  if (!parsed?.equation) {
    return {
      status: 'invalid',
      atoms: [],
      message: `Each coefficient must be a whole number from ${MIN_COEFFICIENT} to ${MAX_COEFFICIENT}.`,
    };
  }
  const atoms = getAtomBalance(parsed.equation);
  if (!isEquationBalanced(parsed.equation)) {
    return { status: 'unbalanced', atoms, message: getBalanceHint(parsed.equation) };
  }
  if (!isSimplestCoefficientRatio(parsed.equation)) {
    return {
      status: 'not-simplest',
      atoms,
      message: 'Every atom balances, but the coefficients share a common factor. Divide them all by it.',
    };
  }
  return {
    status: 'balanced',
    atoms,
    message: `Balanced: the same atoms appear on both sides, so mass is conserved. ${entry.note}`,
  };
}

// ---------------------------------------------------------------------------------------------
// Mass readings in open and closed systems
// ---------------------------------------------------------------------------------------------

export type SystemType = 'open' | 'closed';
export type MassDirection = 'increase' | 'decrease' | 'same';

export interface GasCrossing {
  formula: string;
  name: string;
  /** `in` = taken from the air; `out` = released to the air (only matters in an open system). */
  direction: 'in' | 'out';
}

export interface MassCase {
  id: string;
  title: string;
  setup: string;
  system: SystemType;
  /** Balanced parser equation with coefficients, for example `4Fe + 3O2 -> 2Fe2O3`. */
  equation: string;
  /** Formula of the reactant whose stated mass fully reacts. */
  limitingFormula: string;
  limitingMassG: number;
  /** Container, lid, stand or other apparatus on the balance. */
  containerMassG: number;
  /** Other material on the balance at the start (excess reagent, water, unburnt wax). */
  otherStartingMassG: number;
  otherStartingLabel: string;
  gases: GasCrossing[];
  explanation: string;
  misconception: string;
  boundary: string;
}

export const MASS_DIRECTION_LABELS: Record<MassDirection, string> = {
  increase: 'Mass increases',
  decrease: 'Mass decreases',
  same: 'Mass stays the same',
};

export const MASS_DIRECTIONS: MassDirection[] = ['increase', 'decrease', 'same'];

const CARBONATE_ACID = 'CaCO3 + 2HCl -> CaCl2 + H2O + CO2';
const TABLET_REACTION = '3NaHCO3 + C6H8O7 -> Na3C6H5O7 + 3H2O + 3CO2';
const CARBON_DIOXIDE_OUT: GasCrossing = { formula: 'CO2', name: 'carbon dioxide', direction: 'out' };
const OXYGEN_IN: GasCrossing = { formula: 'O2', name: 'oxygen', direction: 'in' };

export const MASS_CASES: MassCase[] = [
  {
    id: 'steel-wool',
    title: 'Steel wool burns in open air',
    setup: '1.00 g of steel wool (modelled as pure iron) is heated on a heatproof mat on the balance.',
    system: 'open',
    equation: '4Fe + 3O2 -> 2Fe2O3',
    limitingFormula: 'Fe',
    limitingMassG: 1,
    containerMassG: 50,
    otherStartingMassG: 0,
    otherStartingLabel: '',
    gases: [OXYGEN_IN],
    explanation:
      'The iron combines with oxygen from the air. That oxygen was not on the balance before, so the solid gets heavier.',
    misconception:
      'Burning paper loses mass, but burning iron gains it: the product is a solid oxide that keeps the oxygen it took from the air.',
    boundary: 'Real steel wool only partly reacts; the model assumes all the iron forms iron(III) oxide.',
  },
  {
    id: 'carbonate-acid-open',
    title: 'Marble chips in acid, open beaker',
    setup: '2.00 g of calcium carbonate is added to 50.00 g of excess dilute hydrochloric acid in an open beaker.',
    system: 'open',
    equation: CARBONATE_ACID,
    limitingFormula: 'CaCO3',
    limitingMassG: 2,
    containerMassG: 100,
    otherStartingMassG: 50,
    otherStartingLabel: 'dilute hydrochloric acid (excess)',
    gases: [CARBON_DIOXIDE_OUT],
    explanation: 'Carbon dioxide gas fizzes out of the open beaker, so its mass leaves the balance.',
    misconception:
      'No mass is destroyed. The carbon dioxide bubbles carry mass away into the room, so the reading goes down.',
    boundary: 'The model assumes all the carbon dioxide escapes; a little stays dissolved in the acid.',
  },
  {
    id: 'carbonate-acid-sealed',
    title: 'Marble chips in acid, sealed flask',
    setup:
      '0.20 g of calcium carbonate is tipped into 50.00 g of excess dilute hydrochloric acid inside a rigid stoppered flask.',
    system: 'closed',
    equation: CARBONATE_ACID,
    limitingFormula: 'CaCO3',
    limitingMassG: 0.2,
    containerMassG: 150,
    otherStartingMassG: 50,
    otherStartingLabel: 'dilute hydrochloric acid (excess)',
    gases: [CARBON_DIOXIDE_OUT],
    explanation:
      'The same reaction happens, but the carbon dioxide is trapped in the flask. Everything stays on the balance, so the mass is unchanged.',
    misconception:
      'Gas still forms, but it cannot leave a sealed flask. Atoms are only rearranged, so the total mass stays the same.',
    boundary: 'A small amount is used so the pressure stays safe. A balloon would add a small buoyancy error.',
  },
  {
    id: 'magnesium-crucible',
    title: 'Magnesium ribbon in a crucible',
    setup: '0.24 g of magnesium ribbon is heated in a crucible; the lid is lifted now and then to let air in.',
    system: 'open',
    equation: '2Mg + O2 -> 2MgO',
    limitingFormula: 'Mg',
    limitingMassG: 0.24,
    containerMassG: 25,
    otherStartingMassG: 0,
    otherStartingLabel: '',
    gases: [OXYGEN_IN],
    explanation:
      'Oxygen from the air joins the magnesium to form magnesium oxide, so the crucible contents gain mass.',
    misconception:
      'The bright flame looks like the metal is being used up, but the white ash contains the magnesium plus oxygen from the air.',
    boundary: 'Some magnesium also reacts with nitrogen in real crucibles; the model forms only magnesium oxide.',
  },
  {
    id: 'precipitation',
    title: 'Precipitate forms in a beaker',
    setup:
      'A solution containing 1.60 g of copper(II) sulfate is poured into 50.00 g of excess sodium hydroxide solution. Both are on the balance.',
    system: 'open',
    equation: 'CuSO4 + 2NaOH -> Cu(OH)2 + Na2SO4',
    limitingFormula: 'CuSO4',
    limitingMassG: 1.6,
    containerMassG: 100,
    otherStartingMassG: 50,
    otherStartingLabel: 'sodium hydroxide solution (excess) and water',
    gases: [],
    explanation:
      'A blue solid forms, but no gas enters or leaves. All the atoms stay in the beaker, so the mass is unchanged even though the beaker is open.',
    misconception:
      'A new solid appearing does not add mass from nowhere. It forms from ions already in the solutions, and no gas escapes.',
    boundary: 'The copper(II) sulfate mass is the dissolved solid; the water it is dissolved in is counted with the other solution.',
  },
  {
    id: 'candle',
    title: 'A candle burns on the balance',
    setup: 'A candle in a holder burns until 0.50 g of wax (modelled as C₂₅H₅₂) has reacted.',
    system: 'open',
    equation: 'C25H52 + 38O2 -> 25CO2 + 26H2O',
    limitingFormula: 'C25H52',
    limitingMassG: 0.5,
    containerMassG: 20,
    otherStartingMassG: 9.5,
    otherStartingLabel: 'unburnt candle wax',
    gases: [OXYGEN_IN, CARBON_DIOXIDE_OUT, { formula: 'H2O', name: 'water vapour', direction: 'out' }],
    explanation:
      'Oxygen comes in from the air, but carbon dioxide and water vapour leave. More mass leaves than enters, so the candle gets lighter.',
    misconception:
      'The wax is not destroyed. Its atoms leave as carbon dioxide and water vapour, which together weigh more than the wax burnt.',
    boundary: 'Paraffin wax is a mixture; C₂₅H₅₂ is a typical model molecule. The model assumes complete combustion.',
  },
  {
    id: 'tablet-open',
    title: 'Fizzy tablet in an open cup',
    setup:
      'A fizzy antacid-style tablet containing 1.00 g of sodium hydrogencarbonate and excess citric acid drops into water in an open cup.',
    system: 'open',
    equation: TABLET_REACTION,
    limitingFormula: 'NaHCO3',
    limitingMassG: 1,
    containerMassG: 150,
    otherStartingMassG: 1.5,
    otherStartingLabel: 'citric acid (excess) and tablet filler',
    gases: [CARBON_DIOXIDE_OUT],
    explanation: 'The fizz is carbon dioxide escaping from the open cup, so the reading falls.',
    misconception: 'The bubbles are a gas product with mass. When they leave the cup, their mass leaves the balance.',
    boundary: 'The water in the cup is counted with the container mass. The model assumes all the gas escapes.',
  },
  {
    id: 'tablet-sealed',
    title: 'Fizzy tablet in a sealed bottle',
    setup:
      'The same tablet drops into water in a sturdy bottle and the cap is screwed on straight away.',
    system: 'closed',
    equation: TABLET_REACTION,
    limitingFormula: 'NaHCO3',
    limitingMassG: 1,
    containerMassG: 180,
    otherStartingMassG: 1.5,
    otherStartingLabel: 'citric acid (excess) and tablet filler',
    gases: [CARBON_DIOXIDE_OUT],
    explanation:
      'The carbon dioxide stays inside the bottle, so nothing leaves the balance and the mass does not change.',
    misconception:
      'The same gas forms as in the open cup, but the cap keeps it on the balance. Mass is conserved in a closed system.',
    boundary: 'The bottle needs headspace because the pressure rises. Teacher demonstration only.',
  },
  {
    id: 'copper-carbonate',
    title: 'Heating copper(II) carbonate',
    setup: '1.00 g of green copper(II) carbonate is heated in an open test tube until it turns black.',
    system: 'open',
    equation: 'CuCO3 -> CuO + CO2',
    limitingFormula: 'CuCO3',
    limitingMassG: 1,
    containerMassG: 20,
    otherStartingMassG: 0,
    otherStartingLabel: '',
    gases: [CARBON_DIOXIDE_OUT],
    explanation:
      'The carbonate decomposes into black copper(II) oxide and carbon dioxide gas, which escapes from the open tube.',
    misconception:
      'The colour change shows a new substance, and the lost mass is the carbon dioxide that left the tube.',
    boundary: 'The model assumes the carbonate fully decomposes.',
  },
];

export interface SpeciesMass {
  formula: string;
  side: 'reactant' | 'product';
  massG: number;
  gas?: GasCrossing;
}

export interface MassModelResult {
  beforeG: number;
  afterG: number;
  changeG: number;
  direction: MassDirection;
  /** Mass of every reacting species, from the limiting reactant and the equation. */
  species: SpeciesMass[];
  reactantTotalG: number;
  productTotalG: number;
  /** Gas mass that crosses the system boundary (zero crossings in a closed system). */
  crossings: { gas: GasCrossing; massG: number }[];
}

function coefficientOf(equation: ParsedEquation, formula: string, side: 'left' | 'right'): number | null {
  const compound = equation[side].find((entry) => entry.formula === formula);
  return compound ? compound.coefficient : null;
}

export function directionFromChange(changeG: number): MassDirection {
  if (Math.abs(changeG) < SAME_THRESHOLD_G) return 'same';
  return changeG > 0 ? 'increase' : 'decrease';
}

/**
 * Deterministic mass model. Fails closed (null) when the equation is unbalanced, a formula has no
 * molar mass, or a named gas or limiting reactant is not in the equation.
 */
export function massModel(entry: MassCase): MassModelResult | null {
  const outcome = evaluateMassModel(entry);
  return 'model' in outcome ? outcome.model : null;
}

/** The same model with a specific reason when it fails closed, for learner-facing messages. */
export function evaluateMassModel(entry: MassCase): { model: MassModelResult } | { reason: string } {
  const { equation } = parseEquation(entry.equation);
  if (!equation) return { reason: 'The equation could not be read, so no reading is shown.' };
  if (!isEquationBalanced(equation)) return { reason: 'The equation is not balanced, so the masses cannot be worked out.' };
  if (!(entry.limitingMassG > 0)) return { reason: 'The reactant mass must be more than 0 g.' };
  const limitingCoefficient = coefficientOf(equation, entry.limitingFormula, 'left');
  const limitingMolar = molarMass(entry.limitingFormula);
  if (limitingCoefficient === null) return { reason: `${entry.limitingFormula} is not a reactant in this equation.` };
  if (limitingMolar === null) return { reason: `${entry.limitingFormula} has an element without a reviewed atomic mass.` };
  const molesPerCoefficient = entry.limitingMassG / limitingMolar / limitingCoefficient;

  const species: SpeciesMass[] = [];
  for (const [side, compounds] of [
    ['reactant', equation.left],
    ['product', equation.right],
  ] as const) {
    for (const compound of compounds) {
      const molar = molarMass(compound.formula);
      if (molar === null) return { reason: `${compound.formula} has an element without a reviewed atomic mass.` };
      const gas = entry.gases.find(
        (candidate) =>
          candidate.formula === compound.formula &&
          (candidate.direction === 'in') === (side === 'reactant'),
      );
      species.push({
        formula: compound.formula,
        side,
        massG: molesPerCoefficient * compound.coefficient * molar,
        ...(gas ? { gas } : {}),
      });
    }
  }
  const everyGasMatched = entry.gases.every((gas) => species.some((item) => item.gas === gas));
  if (!everyGasMatched) return { reason: 'A gas named for this case is not in the equation, so the balance change cannot be worked out.' };

  const crossings =
    entry.system === 'open'
      ? species.filter((item) => item.gas).map((item) => ({ gas: item.gas as GasCrossing, massG: item.massG }))
      : [];
  const changeG = crossings.reduce(
    (total, crossing) => total + (crossing.gas.direction === 'in' ? crossing.massG : -crossing.massG),
    0,
  );
  const beforeG = entry.containerMassG + entry.limitingMassG + entry.otherStartingMassG;
  const sum = (side: SpeciesMass['side']) =>
    species.filter((item) => item.side === side).reduce((total, item) => total + item.massG, 0);
  return {
    model: {
      beforeG,
      afterG: beforeG + changeG,
      changeG,
      direction: directionFromChange(changeG),
      species,
      reactantTotalG: sum('reactant'),
      productTotalG: sum('product'),
      crossings,
    },
  };
}

export function checkMassPrediction(entry: MassCase, prediction: MassDirection) {
  const model = massModel(entry);
  if (!model) return null;
  const correct = model.direction === prediction;
  return {
    correct,
    expected: model.direction,
    model,
    feedback: correct ? entry.explanation : entry.misconception,
  };
}

export function formatGrams(value: number): string {
  return `${value.toFixed(2)} g`;
}
